<script lang="ts">
  import { Overlay } from '@athena-lib/Overlay';
  import clsx from 'clsx';
  import { onDestroy } from 'svelte';
  import { focusTrap } from '@athena-lib/focusTrap.svelte';
  import { Close } from '@athena-lib/Icons';
  import { getCommonTranslations } from '@athena-lib/commonTranslationsContext';
  import type { ModalProps } from './types';

  let {
    children,
    closable,
    showModal,
    class: className,
    size = 'md',
    backgroundScroll = true,
    label,
    center,
    height,
    onClose = () => {},
  }: ModalProps = $props();
  let isOpen = $derived(showModal ?? false);
  const translations = getCommonTranslations();

  $effect.pre(() => {
    if (!backgroundScroll) {
      document.body.classList.add('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  });

  const sizeClass = $derived.by(() => {
    if (size === 'none') {
      return '';
    }

    const sizes: Record<string, string> = {
      sm: clsx('lg:w-128', 'md:w-96'),
      md: clsx('lg:w-[1024px]', 'md:w-128'),
      lg: clsx('lg:w-[1536px]', 'md:w-168'),
      xlg: clsx('lg:w-full', 'md:w-168'),
      full: clsx('lg:w-full', 'md:w-full'),
    };

    return clsx(sizes[size], 'max-w-full');
  });

  function close() {
    showModal = false;
    onClose();
  }

  onDestroy(() => {
    if (showModal) {
      onClose();
    }
  });
</script>

<Overlay {isOpen} {onClose} label={label ?? 'Modal'}>
  <div
    class={clsx(
      'fixed px-2',
      height,
      center
        ? 'left-1/2 top-1/2 w-full max-w-page-max -translate-x-1/2 -translate-y-1/2'
        : 'inset-x-0 top-10 lg:top-1/4',
    )}
  >
    <div
      use:focusTrap
      class={clsx(
        className,
        sizeClass,
        'relative mx-auto max-h-[90%] overflow-auto rounded bg-white shadow-lg',
      )}
    >
      {#if closable}
        <div class="flex flex-col items-end">
          <button
            type="button"
            class="rounded px-4 py-2 text-4xl font-light text-st-black"
            onclick={close}
            aria-label={translations.closeText}
          >
            <Close sizeClass="h-6 w-6"></Close>
          </button>
        </div>
      {/if}
      {#if children}
        {@render children()}
      {/if}
    </div>
  </div>
</Overlay>
